import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Scale, ArrowLeft } from 'lucide-react'
import { config } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface OddEvenRatio {
  odd_count: number
  even_count: number
  frequency: number
  percentage: number
}

const OddEvenAnalysisPage = () => {
  const [selectedRange, setSelectedRange] = useState(0) // 0 = 10, 1 = 30, 2 = 50
  const [analysisData, setAnalysisData] = useState<Record<number, any>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ranges = [10, 30, 50]
  const scopeMap = ['recent_10', 'recent_30', 'recent_50']

  useEffect(() => {
    fetchAnalysis(ranges[selectedRange])
  }, [selectedRange])

  const fetchAnalysis = async (range: number) => {
    try {
      setLoading(true)
      setError(null)

      const scope = scopeMap[selectedRange]
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/analysis/odd-even-ratio?scope=${scope}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      
      if (!data.ratios || data.ratios.length === 0) {
        throw new Error('분석할 데이터가 없습니다. 로또 결과가 충분히 쌓이면 분석이 표시됩니다.')
      }

      setAnalysisData((prev) => ({
        ...prev,
        [range]: data
      }))
    } catch (err) {
      console.error('Error fetching analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const currentData = analysisData[ranges[selectedRange]]

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="홀짝 비율 통계"
        description="역대 당첨번호의 홀/짝 비율을 통계로 확인하고, 자주 등장하는 조합 패턴을 참고하세요."
        canonicalPath="/analysis/odd-even"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Scale className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                홀짝 비율 통계
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              역대 1등 번호의 홀짝 비율 분석을 통해 황금 밸런스를 찾아보세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Range Selector */}
      <section className="py-8 px-4 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900">분석 기간 선택</h2>
            <div className="flex gap-2">
              {ranges.map((range, index) => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(index)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    selectedRange === index
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  최근 {range}회
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Content */}
      {!loading && !error && currentData && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Golden Balance */}
          {currentData.golden_balance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-2xl p-8 text-center shadow-2xl"
            >
              <div className="flex items-center justify-center mb-4">
                <Scale className="w-12 h-12 text-white mr-3" />
                <h2 className="text-3xl font-black text-white">황금 밸런스</h2>
              </div>
              <div className="text-6xl font-black text-white mb-2">
                홀{currentData.golden_balance.odd_count} : 짝{currentData.golden_balance.even_count}
              </div>
              <div className="text-xl text-white/90">
                {currentData.golden_balance.frequency}회 출현 ({currentData.golden_balance.percentage.toFixed(1)}%)
              </div>
            </motion.div>
          )}

          {/* Ratio Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              홀짝 비율 분포
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentData.ratios.map((ratio: OddEvenRatio, index: number) => (
                <motion.div
                  key={`${ratio.odd_count}-${ratio.even_count}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className={`rounded-2xl p-6 text-center shadow-lg transition-all duration-300 hover:shadow-2xl ${
                    index === 0
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white scale-105'
                      : 'bg-white text-gray-900'
                  }`}
                >
                  <div className="text-4xl font-black mb-2">
                    홀{ratio.odd_count} : 짝{ratio.even_count}
                  </div>
                  <div className={`text-lg mb-2 ${index === 0 ? 'text-white/90' : 'text-gray-600'}`}>
                    {ratio.frequency}회 출현
                  </div>
                  <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                    index === 0 ? 'bg-white/30' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {ratio.percentage.toFixed(1)}%
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Visual Balance Scale */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              홀짝 비율 시각화
            </h3>
            <div className="space-y-4">
              {currentData.ratios.map((ratio: OddEvenRatio) => (
                <div key={`${ratio.odd_count}-${ratio.even_count}`} className="flex items-center gap-4">
                  <div className="w-32 text-right font-bold text-gray-700">
                    홀{ratio.odd_count} : 짝{ratio.even_count}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-full flex items-center justify-end pr-3 text-white font-bold text-sm transition-all duration-500"
                      style={{ width: `${ratio.percentage}%` }}
                    >
                      {ratio.percentage.toFixed(1)}%
                    </div>
                  </div>
                  <div className="w-20 text-left text-gray-600">
                    {ratio.frequency}회
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg"
          >
            <p className="text-gray-700">
              <strong className="text-yellow-700">⚠️ 안내:</strong> 이 분석은 과거 당첨 번호의 통계적 분석이며, 
              미래 당첨 번호를 예측하거나 보장하지 않습니다. 
              로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
            </p>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default OddEvenAnalysisPage

